import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { 
  ShoppingBag,
  Search,
  TrendingUp,
  TrendingDown,
  Info,
  DollarSign,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Percent,
  Target,
  BarChart3,
} from 'lucide-react';
import { 
  LineChart as RechartsLineChart, 
  ComposedChart,
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Scatter,
  Label,
  ReferenceLine,
} from 'recharts';
import {
  computeLogLogElasticity,
  computeRevenueOptimalPrice,
  buildLoessCurve,
  computeMonthlyElasticities,
  transformMonthlyDataForElasticity,
} from '../../utils/elasticity';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

  const PAGE_SIZE = 50;

const DEMO_BRAND_IDS = new Set([
  '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a',
  'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4',
]);

const GA_ECOM_BRAND_ID = '062be473-3b2b-4bad-a2e3-a31995e02d57';

// Approximate monthly USD/TRY rates (updated Feb 2026)
  const DEFAULT_USD_RATES = {
  '2026-02': 36.45,
  '2026-01': 36.20,
  '2025-12': 35.85,
  '2025-11': 35.50,
  '2025-10': 35.20,
  '2025-09': 34.80,
  '2025-08': 34.50,
  '2025-07': 34.15,
  '2025-06': 33.80,
  '2025-05': 33.50,
  '2025-04': 33.10,
  '2025-03': 32.80,
  '2025-02': 32.45,
  '2025-01': 32.10,
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const getMonthKey = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const pct = (v) => (v === null || v === undefined || Number.isNaN(v)) ? '—' : `${(v * 100).toFixed(1)}%`;

// ─────────────────────────────────────────────
// DEMO DATA BUILDER (minimal, only for demo brand)
// ─────────────────────────────────────────────

function buildDemoData() {
    const months = [];
    const now = new Date();
    for (let i = 7; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      months.push({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 });
    }
    const names = [
    'Classic Runner Pro', 'Urban Flex Sneaker', 'Comfort Walk Lite',
    'Trail Blazer X', 'Sport Boost Max', 'Metro Slide',
    'Cloud Step Elite', 'Balance Arch Fit', 'Speed Dash',
    'Endurance Run', 'Street Flow', 'Aero Glide',
    ];
    const rows = [];
  const prng = (s) => { let x = Math.sin(s) * 10000; return x - Math.floor(x); };
  names.forEach((name, i) => {
    const basePrice = 8000 + i * 1200;
    const baseDemand = 180 + (i % 5) * 30;
    months.forEach(({ year, month }, m) => {
      const seed = i * 17 + m * 31;
      const season = 1 + 0.06 * Math.sin((m + i) * Math.PI / 4);
      const noiseP = 1 + (prng(seed + 1) - 0.5) * 0.12;
      const noiseQ = 1 + (prng(seed + 2) - 0.5) * 0.10;
      const price = Math.max(6000, Math.round(basePrice * season * noiseP));
      const demand = Math.max(80, Math.round(baseDemand * noiseQ));
        rows.push({
        itemId: `DEMO-${1000 + i}`,
        itemName: name,
        itemBrand: 'Demo Brand',
          itemCategory: 'Footwear',
        year, month,
        totalRevenue: price * demand,
          totalItemsPurchased: demand,
        totalItemsViewed: demand * (8 + (i % 4)),
        });
    });
  });
    return rows;
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export default function PriceElasticityOfDemand() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(1);
  const [currency, setCurrency] = useState('TRY');

  const [usdRatesByMonth] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('usdRatesByMonth') || 'null');
      return stored && Object.keys(stored).length ? stored : DEFAULT_USD_RATES;
    } catch {
      return DEFAULT_USD_RATES;
    }
  });

  // ── Currency helpers ──
  const getUsdRate = useCallback((monthKey) => {
    const rate = usdRatesByMonth?.[monthKey];
    return Number(rate) > 0 ? Number(rate) : null;
  }, [usdRatesByMonth]);

  const convertValue = useCallback((value, monthKey) => {
    if (currency === 'USD') {
      const rate = getUsdRate(monthKey);
      if (rate) return value / rate;
    }
    return value;
  }, [currency, getUsdRate]);

  const formatCurrency = useCallback((value, monthKey) => {
    const v = convertValue(value, monthKey);
    return currency === 'USD'
      ? `$${Math.round(v).toLocaleString('en-US')}`
      : `₺${Math.round(v).toLocaleString('en-US')}`;
  }, [currency, convertValue]);

  const getLatestMonthKey = useCallback((product) => {
    const prices = product?.historicalPrices;
    if (!prices || prices.length === 0) return getMonthKey(new Date().toISOString());
    return getMonthKey(prices[prices.length - 1].date);
  }, []);

  // ── Fetch Data ──
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) { setIsLoading(false); return; }

        // Demo brands
        if (DEMO_BRAND_IDS.has(brandId)) {
          const transformed = transformMonthlyDataForElasticity(buildDemoData());
          setProducts(transformed);
          setSelectedProduct(transformed[0] || null);
          return;
        }

        const isGaBrand = brandId === GA_ECOM_BRAND_ID;
        const limit = 1000;
        const apiUrl = isGaBrand
          ? `https://api.glowytics.com/api/google-ads/ga-ecommerce-item-monthly?dateRange=all&limit=${limit}`
          : `https://api.glowytics.com/api/google-ads/monthly-item-performance?dateRange=all&limit=${limit}`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        });

        if (!response.ok) throw new Error(`API error: ${response.status}`);

        const data = await response.json();
        let apiData = Array.isArray(data) ? data : (data?.data || []);

        // Normalize GA e-com fields
        if (isGaBrand && Array.isArray(apiData)) {
          apiData = apiData.map(row => ({
            ...row,
            totalRevenue: row.totalRevenue ?? Number(row.itemRevenue || 0),
            totalItemsViewed: row.totalItemsViewed ?? Number(row.itemsViewed || 0),
            totalItemsPurchased: row.totalItemsPurchased ?? Number(row.itemsPurchased || 0),
          }));
        }

        if (!Array.isArray(apiData) || apiData.length === 0) {
          setProducts([]); setSelectedProduct(null); return;
        }

        const transformed = transformMonthlyDataForElasticity(apiData);
        setProducts(transformed);
        if (transformed.length > 0) setSelectedProduct(transformed[0]);
      } catch (error) {
        console.error('Error fetching product data:', error.message);
        setProducts([]); setSelectedProduct(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // ── Filtering & Pagination ──
  const filteredProducts = useMemo(() => {
    const q = (searchQuery || '').toLowerCase();
    return products.filter(p => {
      const name = String(p.name || '').toLowerCase();
      const cat = String(p.category || '').toLowerCase();
      const id = String(p.id || '').toLowerCase();
      return name.includes(q) || cat.includes(q) || id.includes(q);
    });
  }, [products, searchQuery]);

  useEffect(() => { setPageIndex(1); }, [searchQuery, products]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pagedProducts = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, pageIndex]);

  // ── Memoized calculations for selected product ──
  const monthlyElasticities = useMemo(() => {
    if (!selectedProduct?.historicalPrices) return [];
    return computeMonthlyElasticities(selectedProduct.historicalPrices);
  }, [selectedProduct]);

  const loessRevenue = useMemo(() => {
    if (!selectedProduct?.historicalPrices) return { curve: [], points: [], optimum: null };
    return buildLoessCurve(selectedProduct.historicalPrices, 'revenue');
  }, [selectedProduct]);

  const loessDemand = useMemo(() => {
    if (!selectedProduct?.historicalPrices) return { curve: [], points: [], optimum: null };
    return buildLoessCurve(selectedProduct.historicalPrices, 'demand');
  }, [selectedProduct]);

  // Chart data (exclude current month)
  const chartData = useMemo(() => {
    if (!selectedProduct?.historicalPrices) return [];
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return selectedProduct.historicalPrices
      .filter(p => getMonthKey(p.date) !== currentKey)
      .map(p => {
      const mk = getMonthKey(p.date);
      return {
          date: p.date,
          price: currency === 'USD' ? convertValue(p.price, mk) : p.price,
          demand: p.demand,
        };
      });
  }, [selectedProduct, currency, convertValue]);

  // ── Loading / Empty ──
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-[500px]">
        <div className="text-center">
          <div className="loading-logo-container mb-4">
            <img src="/logoicon.png" alt="Loading" className="loading-logo mx-auto" />
          </div>
          <p className="text-gray-500">Loading product elasticity data...</p>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="p-6 flex justify-center items-center h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-2">No Product Data</h2>
          <p className="text-gray-500">
            Price elasticity analysis requires product-level performance data with at least 2 months of history.
            Connect your Google Ads or GA4 account first.
          </p>
        </div>
      </div>
    );
  }

  const currSymbol = currency === 'USD' ? '$' : '₺';

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ── */}
        <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm">
          <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </div>
            <div>
            <h1 className="text-2xl font-semibold text-gray-800">Price Elasticity of Demand</h1>
              <p className="text-sm text-gray-500 mt-1">
              Product-level price sensitivity analysis using LOESS smoothing on real performance data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setCurrency('TRY')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                currency === 'TRY' ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >₺ TRY</button>
              <button
                onClick={() => setCurrency('USD')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                currency === 'USD' ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >$ USD</button>
            </div>
          <span className="text-xs text-gray-400">{products.length} products</span>
          </div>
      </div>

      {/* ── Main 2-Column Layout ── */}
      <div className="flex gap-6">

        {/* ── Left: Product List ── */}
        <div className="w-1/4">
          <Card className="rounded-2xl sticky top-6 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>

            <div className="divide-y divide-gray-50 max-h-[calc(100vh-240px)] overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">No products found</div>
              ) : (
                pagedProducts.map(product => {
                  const isActive = selectedProduct?.id === product.id;
                  const mk = getLatestMonthKey(product);
                  const eStyle = getElasticityStyle(product.elasticity);
                  return (
                    <button
                    key={product.id}
                      className={`w-full text-left p-3 transition-colors ${
                        isActive ? 'bg-indigo-50 border-l-3 border-l-indigo-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedProduct(product)}
                  >
                      <div className="flex items-center gap-2.5">
                        {product.imageLink && (
                          <img src={product.imageLink} alt="" className="w-9 h-9 rounded border object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{product.name}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5 truncate">{product.mcCategory || product.category}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-1">
                          <p className="text-xs font-semibold text-gray-800">{formatCurrency(product.currentPrice, mk)}</p>
                          <div className={`text-[10px] font-medium mt-0.5 flex items-center justify-end gap-0.5 ${eStyle.textClass}`}>
                            {product.elasticity.toFixed(2)}
                            {eStyle.smallIcon}
                      </div>
                          </div>
                        </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {filteredProducts.length > PAGE_SIZE && (
              <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-[10px] text-gray-400">
                  {pageIndex}/{totalPages}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                    disabled={pageIndex <= 1}
                    onClick={() => setPageIndex(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                  <button
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                    disabled={pageIndex >= totalPages}
                    onClick={() => setPageIndex(p => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* ── Right: Product Detail ── */}
        <div className="w-3/4 space-y-5">
          {selectedProduct ? (
            <>
              {/* Product Header */}
              <Card className="p-5 rounded-2xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    {selectedProduct.imageLink && (
                      <img src={selectedProduct.imageLink} alt="" className="w-14 h-14 rounded-lg border object-cover" />
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800 leading-snug max-w-[480px] break-words">
                          {selectedProduct.name}
                        </h2>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {(selectedProduct.mcCategory || selectedProduct.category) && (
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] border border-gray-200">
                            {selectedProduct.mcCategory || selectedProduct.category}
                          </span>
                        )}
                        <span className="px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 text-[10px] border border-gray-200">
                          ID: {String(selectedProduct.id)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-5">
                    <KPIBlock
                      label="Total Revenue"
                      value={formatCurrency(selectedProduct.totalRevenue, getLatestMonthKey(selectedProduct))}
                    />
                    <KPIBlock
                      label="Current Price"
                      value={formatCurrency(selectedProduct.currentPrice, getLatestMonthKey(selectedProduct))}
                    />
                    <KPIBlock
                      label="Last Demand"
                      value={`${selectedProduct.currentDemand} units`}
                    />
                      </div>
                    </div>
              </Card>

              {/* Elasticity Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <ElasticityCard
                  elasticity={selectedProduct.elasticity}
                />
                <Card className="p-4 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <Target className="w-4 h-4 text-amber-600" />
                  </div>
                    <span className="text-xs text-gray-500">Optimum Price</span>
                </div>
                  <p className="text-2xl font-bold text-gray-800">
                    {formatCurrency(selectedProduct.optimumPrice, getLatestMonthKey(selectedProduct))}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                      <span className={`text-xs font-medium ${
                      selectedProduct.optimumPrice >= selectedProduct.currentPrice ? 'text-green-600' : 'text-red-500'
                      }`}>
                      {selectedProduct.optimumPrice >= selectedProduct.currentPrice ? '+' : ''}
                        {((selectedProduct.optimumPrice / selectedProduct.currentPrice - 1) * 100).toFixed(1)}%
                      </span>
                    <span className="text-xs text-gray-400">vs current</span>
                    </div>
                  <p className="text-[10px] text-gray-400 mt-1">Revenue-maximizing price (LOESS model)</p>
                </Card>
                <Card className="p-4 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-emerald-50">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                    <span className="text-xs text-gray-500">Revenue Impact</span>
                  </div>
                  <p className={`text-2xl font-bold ${
                    selectedProduct.potentialGrowth >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {selectedProduct.potentialGrowth > 0 ? '+' : ''}{selectedProduct.potentialGrowth}%
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-xs font-medium text-gray-700">
                      {formatCurrency(selectedProduct.optimumRevenue, getLatestMonthKey(selectedProduct))}
                      </span>
                    <span className="text-xs text-gray-400">est. monthly</span>
                    </div>
                  <p className="text-[10px] text-gray-400 mt-1">If pricing at optimum point</p>
                </Card>
                </div>

              {/* Price vs Demand Timeline */}
              <Card className="p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Price vs Demand Timeline</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Monthly average price and units sold</p>
                      </div>
                  <div className="flex items-center gap-3">
                    <LegendDot color="bg-indigo-500" label={`Price (${currSymbol})`} />
                    <LegendDot color="bg-emerald-500" label="Demand" />
                    <LegendDot color="bg-amber-400" label="Optimum" dashed />
                      </div>
                    </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          stroke="#9ca3af"
                        tick={{ fontSize: 11 }}
                        >
                        <Label value="Month" offset={-15} position="insideBottom" fill="#9ca3af" fontSize={11} />
                        </XAxis>
                        <YAxis 
                          yAxisId="left" 
                          orientation="left" 
                        stroke="#6366f1"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${currSymbol}${Math.round(v).toLocaleString()}`}
                      >
                        <Label value={`Price (${currSymbol})`} angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fill: '#6366f1', fontSize: 11 }} />
                        </YAxis>
                        <YAxis 
                          yAxisId="right" 
                          orientation="right" 
                        stroke="#10b981"
                        tick={{ fontSize: 11 }}
                      >
                        <Label value="Units" angle={90} position="insideRight" style={{ textAnchor: 'middle', fill: '#10b981', fontSize: 11 }} />
                        </YAxis>
                        <Tooltip 
                        contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
                          formatter={(value, name) => {
                          if (name === 'price') return [`${currSymbol}${Math.round(value).toLocaleString()}`, 'Price'];
                          if (name === 'demand') return [`${value} units`, 'Demand'];
                            return [value, name];
                          }}
                        labelFormatter={formatDate}
                        />
                        <ReferenceLine 
                        y={convertValue(selectedProduct.optimumPrice, getLatestMonthKey(selectedProduct))}
                          yAxisId="left" 
                          stroke="#f59e0b" 
                        strokeDasharray="4 4"
                        label={{ value: 'Optimum', position: 'insideBottomRight', fill: '#f59e0b', fontSize: 10 }}
                      />
                      <Line type="monotone" dataKey="price" stroke="#6366f1" strokeWidth={2.5} yAxisId="left" name="price" dot={{ r: 4, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }} />
                      <Line type="monotone" dataKey="demand" stroke="#10b981" strokeWidth={2.5} yAxisId="right" name="demand" dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  </div>
              </Card>

              {/* LOESS Optimization Curve */}
              <Card className="p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Price Optimization Curve (LOESS)</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Revenue and demand response across observed price range
                    </p>
              </div>
                  <div className="flex items-center gap-3">
                    <LegendDot color="bg-indigo-500" label="Revenue" shape="circle" />
                    <LegendDot color="bg-emerald-500" label="Demand" shape="square" />
                    <LegendDot color="bg-amber-400" label="Optimum" dashed />
                  </div>
                </div>
                <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart margin={{ top: 10, right: 50, left: 20, bottom: 20 }}>
                      <CartesianGrid stroke="#f3f4f6" strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="price" 
                      type="number" 
                      domain={['dataMin', 'dataMax']} 
                      stroke="#9ca3af" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${currSymbol}${Math.round(v).toLocaleString()}`}
                    >
                        <Label value={`Price (${currSymbol})`} offset={-10} position="insideBottom" fill="#9ca3af" fontSize={11} />
                    </XAxis>
                    <YAxis 
                      yAxisId="revenue"
                      orientation="left"
                        stroke="#6366f1"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${currSymbol}${(v / 1000).toFixed(0)}k`}
                      >
                        <Label value="Revenue" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fill: '#6366f1', fontSize: 11 }} />
                    </YAxis>
                    <YAxis 
                      yAxisId="demand"
                      orientation="right"
                        stroke="#10b981"
                        tick={{ fontSize: 11 }}
                    >
                        <Label value="Demand" angle={90} position="insideRight" style={{ textAnchor: 'middle', fill: '#10b981', fontSize: 11 }} />
                    </YAxis>
                    <Tooltip
                      formatter={(val, name) => {
                          if (name === 'Revenue Curve' || name === 'Observed Revenue')
                            return [`${currSymbol}${Math.round(val).toLocaleString()}`, 'Revenue'];
                          if (name === 'Demand Curve' || name === 'Observed Demand')
                            return [`${Math.round(val)} units`, 'Demand'];
                        return [val, name];
                      }}
                        labelFormatter={(lbl) => `Price: ${currSymbol}${Math.round(Number(lbl)).toLocaleString()}`}
                        contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}
                      />

                      {/* Revenue scatter + curve */}
                      <Scatter yAxisId="revenue" name="Observed Revenue" data={loessRevenue.points} fill="#4f46e5" shape="circle" r={5} stroke="#312e81" strokeWidth={1.5} />
                      <Line yAxisId="revenue" data={loessRevenue.curve} type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2.5} dot={false} name="Revenue Curve" />

                      {/* Demand scatter + curve */}
                      <Scatter yAxisId="demand" name="Observed Demand" data={loessDemand.points} fill="#059669" shape="square" r={5} stroke="#064e3b" strokeWidth={1.5} />
                      <Line yAxisId="demand" data={loessDemand.curve} type="monotone" dataKey="demand" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Demand Curve" />

                      {/* Optimum line */}
                      {selectedProduct.optimumPrice && (
                        <ReferenceLine 
                          x={selectedProduct.optimumPrice}
                          yAxisId="revenue"
                          stroke="#f59e0b" 
                          strokeDasharray="4 4"
                          strokeWidth={2}
                          label={{ 
                            value: `OPTIMUM: ${currSymbol}${Math.round(selectedProduct.optimumPrice).toLocaleString()}`,
                            position: 'insideTop', 
                            fill: '#f59e0b', 
                            fontSize: 12,
                            fontWeight: 'bold',
                            dy: -10,
                          }} 
                        />
                      )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

                {/* Dynamic explanation */}
                <div className="mt-4 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                  <p className="text-xs text-indigo-800">
                    <strong>How to read this chart:</strong> Blue dots = actual monthly price/revenue combinations. The blue curve (LOESS fit) smooths through these points to estimate revenue at any price.
                    The amber line marks the price that maximizes estimated revenue (±guardrail).
                    {selectedProduct.optimumPrice > selectedProduct.currentPrice
                      ? ` The model suggests a price increase of ${currSymbol}${Math.round(selectedProduct.optimumPrice - selectedProduct.currentPrice).toLocaleString()} could boost revenue.`
                      : selectedProduct.optimumPrice < selectedProduct.currentPrice
                      ? ` The model suggests a price decrease of ${currSymbol}${Math.round(selectedProduct.currentPrice - selectedProduct.optimumPrice).toLocaleString()} could improve volume.`
                      : ' Current pricing is already near optimal.'}
                  </p>
                </div>
              </Card>

              {/* Dynamic Model Visualization (SVG) */}
              <Card className="p-5 rounded-2xl">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Model Visualization</h3>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <DynamicModelSVG
                      points={loessRevenue.points}
                      curve={loessRevenue.curve}
                      optimumPrice={selectedProduct.optimumPrice}
                      currentPrice={selectedProduct.currentPrice}
                      currSymbol={currSymbol}
                    />
                </div>
                  <div className="flex-1 text-xs text-gray-600 space-y-3">
                    <p className="font-medium text-gray-800">How the LOESS Model Works</p>
                    <ol className="list-decimal list-inside space-y-2.5">
                      <li>
                        <span className="font-semibold text-gray-700">Data Points:</span> Each dot represents one month's actual price and revenue performance.
                      </li>
                      <li>
                        <span className="font-semibold text-indigo-600">Curve Fitting:</span> LOESS (Local Regression) draws a smooth trend through the scatter, answering "what if we tried this price?"
                      </li>
                      <li>
                        <span className="font-semibold text-amber-600">Optimum:</span> The peak of the revenue curve is the price point that maximizes income (within a ±10-20% safety band).
                      </li>
                    </ol>
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100 mt-2">
                      <p className="italic text-indigo-700">
                        The system searches for the "sweet spot" — the price where your revenue curve peaks across historical data.
                </p>
              </div>
            </div>
                </div>
              </Card>

            {/* Monthly Elasticity Table */}
              <Card className="p-5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Monthly Elasticity (MoM)</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Month-over-month price sensitivity</p>
                  </div>
              </div>
              {monthlyElasticities.length === 0 ? (
                  <p className="text-xs text-gray-500">Not enough consecutive months to compute MoM elasticity.</p>
              ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Month</th>
                          <th className="text-right py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Price</th>
                          <th className="text-right py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Demand</th>
                          <th className="text-right py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Revenue</th>
                          <th className="text-right py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Δ Price</th>
                          <th className="text-right py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Δ Demand</th>
                          <th className="text-right py-2.5 px-3 font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Elasticity</th>
                      </tr>
                    </thead>
                      <tbody className="divide-y divide-gray-50">
                      {monthlyElasticities.map((row, idx) => {
                        const mk = getMonthKey(row.period);
                          const eColor = row.elasticity === null || Number.isNaN(row.elasticity)
                            ? 'text-gray-500'
                            : row.elasticity < 0 ? 'text-green-600' : row.elasticity > 0 ? 'text-red-500' : 'text-gray-500';
                        return (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="py-2.5 px-3 text-gray-700 font-medium">{formatDate(row.period)}</td>
                              <td className="py-2.5 px-3 text-right text-gray-700">{formatCurrency(row.price, mk)}</td>
                              <td className="py-2.5 px-3 text-right text-gray-700">{row.demand}</td>
                              <td className="py-2.5 px-3 text-right text-gray-700">{formatCurrency(row.revenue, mk)}</td>
                              <td className="py-2.5 px-3 text-right">
                                <ChangeCell value={row.priceChange} />
                            </td>
                              <td className="py-2.5 px-3 text-right">
                                <ChangeCell value={row.demandChange} />
                            </td>
                              <td className={`py-2.5 px-3 text-right font-semibold ${eColor}`}>
                                {row.elasticity === null || Number.isNaN(row.elasticity) ? '—' : row.elasticity.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              </Card>

              {/* Methodology */}
              <Card className="p-5 rounded-2xl">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Methodology</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-gray-600">
                <div>
                    <ul className="space-y-2">
                      <li><strong>Data:</strong> Monthly <em>price</em> = Revenue / Units, <em>demand</em> = Units sold.</li>
                      <li><strong>Elasticity:</strong> Log-log OLS — <code className="bg-gray-100 px-1 rounded">β = Cov(ln P, ln Q) / Var(ln P)</code></li>
                      <li><strong>Optimum Price:</strong> LOESS smoothing on (P, R=P×Q), peak within ±10-20% guardrail.</li>
                      <li><strong>Impact:</strong> <code className="bg-gray-100 px-1 rounded">(R(p*) − R_current) / R_current</code></li>
                  </ul>
                </div>
                <div>
                    <ul className="space-y-2">
                      <li><strong>LOESS:</strong> Local linear regression with tricube kernel and recency weighting (λ=0.7).</li>
                      <li><strong>Guardrails:</strong> ±10% if last month revenue ≥ P75, else ±20%.</li>
                      <li><strong>Confidence:</strong> Negative elasticity = expected (green). Positive = anomaly/seasonality (red).</li>
                      <li><strong>Note:</strong> Past performance does not guarantee future results.</li>
                  </ul>
                </div>
              </div>
              </Card>
            </>
          ) : (
            <Card className="p-8 rounded-2xl h-full flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-800">Select a product</h3>
                <p className="text-gray-500 mt-1 text-sm">Choose a product from the list to view price elasticity analysis</p>
            </div>
            </Card>
          )}
                </div>
                  </div>
                </div>
  );
}

// ─────────────────────────────────────────────
// SUBCOMPONENTS
// ─────────────────────────────────────────────

function KPIBlock({ label, value }) {
  return (
    <div className="text-right">
      <p className="text-xl font-bold text-gray-800">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
              </div>
  );
}

function ElasticityCard({ elasticity }) {
  const style = getElasticityStyle(elasticity);
  const type = getElasticityType(elasticity);
  return (
    <Card className={`p-4 rounded-2xl border ${style.borderClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-2 rounded-lg ${style.bgClass}`}>
          <Percent className={`w-4 h-4 ${style.iconColor}`} />
            </div>
        <span className="text-xs text-gray-500">Elasticity Coefficient</span>
            </div>
      <p className={`text-2xl font-bold ${style.textClass}`}>
        {elasticity.toFixed(2)}
      </p>
      <div className="flex items-center gap-1.5 mt-1.5">
        {style.smallIcon}
        <span className={`text-xs font-medium ${style.textClass}`}>{type.text}</span>
              </div>
      <p className="text-[10px] text-gray-400 mt-1">{type.description}</p>
    </Card>
  );
}

function LegendDot({ color, label, shape, dashed }) {
  return (
    <div className="flex items-center gap-1.5">
      {dashed ? (
        <div className={`w-3 h-0.5 ${color} border-t-2 border-dashed`} style={{ borderColor: 'currentColor' }}></div>
      ) : shape === 'square' ? (
        <div className={`w-2.5 h-2.5 ${color} rounded-sm`}></div>
      ) : (
        <div className={`w-2.5 h-2.5 ${color} rounded-full`}></div>
      )}
      <span className="text-[10px] text-gray-500">{label}</span>
    </div>
  );
}

function ChangeCell({ value }) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <span className="text-gray-400">—</span>;
  }
  const isPos = value > 0;
  const isNeg = value < 0;
  return (
    <span className={`inline-flex items-center gap-0.5 ${isPos ? 'text-green-600' : isNeg ? 'text-red-500' : 'text-gray-500'}`}>
      {isPos && <ArrowUp className="w-3 h-3" />}
      {isNeg && <ArrowDown className="w-3 h-3" />}
      {pct(value)}
    </span>
  );
}

/**
 * Dynamic SVG that visualizes the actual LOESS curve + data points.
 */
function DynamicModelSVG({ points, curve, optimumPrice, currentPrice, currSymbol }) {
  if (!points || points.length === 0 || !curve || curve.length === 0) {
    return <div className="h-48 flex items-center justify-center text-xs text-gray-400">Not enough data</div>;
  }

  const W = 400, H = 240;
  const pad = { top: 20, right: 20, bottom: 35, left: 50 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  // Data ranges
  const allPrices = [...points.map(p => p.price), ...curve.map(c => c.price)];
  const allRevenues = [...points.map(p => p.revenue), ...curve.map(c => c.revenue)];
  const minP = Math.min(...allPrices);
  const maxP = Math.max(...allPrices);
  const minR = Math.min(...allRevenues) * 0.9;
  const maxR = Math.max(...allRevenues) * 1.05;

  const scaleX = (p) => pad.left + ((p - minP) / (maxP - minP || 1)) * plotW;
  const scaleY = (r) => pad.top + plotH - ((r - minR) / (maxR - minR || 1)) * plotH;

  // Build curve path
  const curvePath = curve
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(c.price).toFixed(1)} ${scaleY(c.revenue).toFixed(1)}`)
    .join(' ');

  // Optimum position
  const optX = scaleX(optimumPrice || currentPrice);
  const curX = scaleX(currentPrice);

  // Y-axis ticks
  const yTicks = 4;
  const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) => minR + ((maxR - minR) * i) / yTicks);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[240px]" preserveAspectRatio="xMidYMid meet">
      {/* Grid */}
      {yTickValues.map((v, i) => (
        <g key={i}>
          <line x1={pad.left} y1={scaleY(v)} x2={W - pad.right} y2={scaleY(v)} stroke="#f3f4f6" strokeWidth="1" />
          <text x={pad.left - 6} y={scaleY(v) + 3} fontSize="8" fill="#9ca3af" textAnchor="end">
            {currSymbol}{(v / 1000).toFixed(0)}k
          </text>
        </g>
      ))}

      {/* Axes */}
      <line x1={pad.left} y1={H - pad.bottom} x2={W - pad.right} y2={H - pad.bottom} stroke="#d1d5db" strokeWidth="1" />
      <line x1={pad.left} y1={pad.top} x2={pad.left} y2={H - pad.bottom} stroke="#d1d5db" strokeWidth="1" />

      {/* Axis labels */}
      <text x={W / 2} y={H - 6} fontSize="9" fill="#9ca3af" textAnchor="middle">Price ({currSymbol})</text>
      <text x={12} y={H / 2} fontSize="9" fill="#9ca3af" textAnchor="middle" transform={`rotate(-90, 12, ${H / 2})`}>Revenue</text>

      {/* LOESS curve */}
      <path d={curvePath} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" />

      {/* Data points */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={scaleX(p.price)}
          cy={scaleY(p.revenue)}
          r="4"
          fill="#6366f1"
          stroke="#fff"
          strokeWidth="1.5"
          opacity="0.8"
        />
      ))}

      {/* Current price line */}
      <line x1={curX} y1={pad.top} x2={curX} y2={H - pad.bottom} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth="1" />
      <text x={curX} y={H - pad.bottom + 12} fontSize="8" fill="#94a3b8" textAnchor="middle">Current</text>

      {/* Optimum line + dot */}
      {optimumPrice && (
        <>
          <line x1={optX} y1={pad.top} x2={optX} y2={H - pad.bottom} stroke="#f59e0b" strokeDasharray="4 4" strokeWidth="1.5" />
          <circle cx={optX} cy={scaleY(points.reduce((best, p) => {
            const rev = p.revenue;
            return rev > best ? rev : best;
          }, 0) * 0.95)} r="6" fill="#f59e0b" stroke="#fff" strokeWidth="2" />
          <text x={optX + 8} y={pad.top + 12} fontSize="9" fill="#f59e0b" fontWeight="bold">Optimum</text>
        </>
      )}
    </svg>
  );
}

// ─────────────────────────────────────────────
// STYLE HELPERS
// ─────────────────────────────────────────────

function getElasticityStyle(e) {
  if (e === null || Number.isNaN(e)) {
    return {
      textClass: 'text-gray-600', bgClass: 'bg-gray-50', borderClass: 'border-gray-200',
      iconColor: 'text-gray-500',
      smallIcon: <ArrowRight className="w-3 h-3 text-gray-500" />,
    };
  }
  if (e < 0) {
    return {
      textClass: 'text-green-700', bgClass: 'bg-green-50', borderClass: 'border-green-200',
      iconColor: 'text-green-600',
      smallIcon: <TrendingUp className="w-3 h-3 text-green-600" />,
    };
  }
  if (e === 0) {
    return {
      textClass: 'text-blue-700', bgClass: 'bg-blue-50', borderClass: 'border-blue-200',
      iconColor: 'text-blue-600',
      smallIcon: <ArrowRight className="w-3 h-3 text-blue-600" />,
    };
  }
  return {
    textClass: 'text-red-600', bgClass: 'bg-red-50', borderClass: 'border-red-200',
    iconColor: 'text-red-500',
    smallIcon: <TrendingDown className="w-3 h-3 text-red-500" />,
  };
}

function getElasticityType(e) {
  const abs = Math.abs(e);
  if (abs < 1) return { text: 'Inelastic', description: 'Demand relatively unaffected by price changes' };
  if (abs === 1) return { text: 'Unit Elastic', description: 'Proportional demand response to price' };
  return { text: 'Elastic', description: 'Demand is highly sensitive to price changes' };
}
